import React, { useState, useEffect } from 'react';

/**
 * Floating variant-switcher pill.
 * Visible only on `/`, `/v2a`, `/v2b`, `/v2c`, `/v2d`.
 * Full page navigation on click so each variant's scoped fonts + tokens load cleanly.
 */
const VARIANTS = [
  { key: 'v1', path: '/', label: 'V1', name: 'Current preview' },
  { key: 'preview', path: '/preview', label: 'P', name: 'Story-driven site' },
  { key: 'v2a', path: '/v2a', label: 'A', name: 'Sales-forward' },
  { key: 'v2b', path: '/v2b', label: 'B', name: 'Human storyteller' },
  { key: 'v2c', path: '/v2c', label: 'C', name: 'Executive briefing' },
  { key: 'v2d', path: '/v2d', label: 'D', name: 'Vanguard hybrid' },
];

export default function VariantSwitcher() {
  const [path, setPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const active = VARIANTS.find((v) => v.path === path);
  if (!active) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9998,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
      data-testid="variant-switcher"
    >
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-full transition-all hover:scale-105"
          style={{
            background: 'rgba(15, 23, 42, 0.92)',
            color: 'white',
            fontSize: '11px',
            fontWeight: 600,
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 8px 24px -4px rgba(0,0,0,0.35)',
          }}
          data-testid="variant-switcher-expand"
          title="Show variant switcher"
        >
          <span style={{ opacity: 0.7 }}>Variants</span>
          <span style={{ fontWeight: 800, letterSpacing: '0.05em' }}>{active.label}</span>
        </button>
      ) : (
        <div
          className="flex items-center gap-1 p-1 rounded-full"
          style={{
            background: 'rgba(15, 23, 42, 0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 8px 24px -4px rgba(0,0,0,0.35)',
          }}
        >
          <div
            className="px-3 text-[10px] font-semibold uppercase tracking-widest select-none"
            style={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Landing
          </div>
          <div className="h-4 w-px" style={{ background: 'rgba(255,255,255,0.15)' }} />
          {VARIANTS.map((v) => {
            const isActive = v.path === path;
            return (
              <a
                key={v.key}
                href={v.path}
                title={`${v.label} — ${v.name}`}
                data-testid={`variant-switcher-${v.key}`}
                className="relative flex items-center justify-center rounded-full transition-all"
                style={{
                  width: v.key === 'v1' ? 'auto' : '32px',
                  height: '32px',
                  padding: v.key === 'v1' ? '0 12px' : '0',
                  background: isActive ? '#7C3AED' : 'transparent',
                  color: isActive ? 'white' : 'rgba(255,255,255,0.7)',
                  fontSize: '12px',
                  fontWeight: isActive ? 800 : 600,
                  textDecoration: 'none',
                  letterSpacing: v.key === 'v1' ? '0.02em' : '0.05em',
                  boxShadow: isActive ? '0 4px 12px -2px rgba(124,58,237,0.5)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                {v.label}
              </a>
            );
          })}
          <div className="h-4 w-px" style={{ background: 'rgba(255,255,255,0.15)' }} />
          <div
            className="px-2.5 text-[10px] select-none"
            style={{ color: 'rgba(255,255,255,0.6)', minWidth: '110px' }}
          >
            {active.name}
          </div>
          <button
            onClick={() => setCollapsed(true)}
            className="flex items-center justify-center rounded-full transition-colors"
            style={{
              width: '24px',
              height: '24px',
              marginLeft: '2px',
              background: 'transparent',
              color: 'rgba(255,255,255,0.5)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: 1,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            data-testid="variant-switcher-collapse"
            title="Collapse"
            aria-label="Collapse"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
